import { useMemo, useState, useCallback } from 'react';
import { FieldMask } from '@weave/schema-gen-ts/dist/google/protobuf/field_mask.pb';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { List, ListListsRequest } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2/service.pb';
import { BulkMessagingMutations, BulkMessagingQueries } from '@frontend/api-bulk-messaging';

export const useContactLists = (accessibleLocationIds: string[], types: CampaignType[]) => {
  const [searchQuery, setSearchQuery] = useState('');
  const request: ListListsRequest = {
    locationId: accessibleLocationIds[0],
    pageSize: 25,
    searchQuery,
    types,
  };

  const queryKey = BulkMessagingQueries.keys.listLists(request);

  const queryResult = BulkMessagingQueries.useListLists(request);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);

  const contactLists = useMemo(() => queryResult.data?.pages.flatMap((page) => page.lists) ?? [], [queryResult.data]);

  const { mutateAsync: mutateCreate } = BulkMessagingMutations.useCreateList(queryKey);
  const { mutateAsync: mutateDelete } = BulkMessagingMutations.useDeleteList(queryKey);
  const { mutateAsync: mutateUpdate } = BulkMessagingMutations.useUpdateList(queryKey);

  const selectContactList = useCallback((listId: string) => {
    setSelectedListIds((prevIds) => [...prevIds, listId]);
  }, []);

  const deselectContactList = useCallback((listId: string) => {
    setSelectedListIds((prevIds) => prevIds.filter((selectedListId) => selectedListId !== listId));
    // TODO: Figure out how to update other campaigns which may now have an orphaned contact list stuck on them that cannot be removed
  }, []);

  const createContactList = useCallback(
    async (list: List) => {
      const response = await mutateCreate({ list });
      if (response.listId) selectContactList(response.listId);
    },
    [mutateCreate, selectContactList]
  );

  const deleteContactList = useCallback(
    async (listId: string) => {
      await mutateDelete({ listId });
      deselectContactList(listId);
    },
    [mutateDelete, deselectContactList]
  );

  const renameContactList = useCallback(
    async (list: List) => {
      await mutateUpdate({ list, updateMask: new FieldMask({ paths: ['list.name'] }) });
    },
    [mutateUpdate]
  );

  return {
    contactLists,
    contactListsQueryResult: queryResult,
    createContactList,
    deleteContactList,
    deselectContactList,
    renameContactList,
    searchQuery,
    selectContactList,
    selectedContactLists: selectedListIds,
    setSelectedContactLists: setSelectedListIds,
    setSearchQuery,
  };
};
